import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjBeatBreath, VdjPost, VdjComfort, VDJ_DROP_FLASH, VDJ_PRESETS } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { BubbleSim, BOX, POP_S, BIRTH_S, GRAVITY, DROP_LIFE, MAX_DROPS, MAX_BUBBLES } from './lib/bubbles-sim.js';
import { filmLutData, FILM_LUT, FILM_H_MAX, N_FILM } from './lib/bubbles-film.js';

// Contrat du rig partagé : interdit. Les bulles ne reflètent que leur club.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'bubbles',
  family: 'organic',
  danceability: 0.66,
  energyLevel: 0.50,
  bpmMin: 85,
  bpmMax: 130,
  movement: 0.50,
  tonality: 0.75,
  enabled: true
};

// BUBBLES : des bulles de savon géantes et petites qui flottent devant un club
// flou (néons, bokeh, le nom de l'artiste en néon au fond). Pop, house
// mélodique, disco, électro-pop, funk. Règles R1 à R19, preset lounge de
// vdjLook, party au-dessus de 0,7 d'intensité (adapt.js). (Ne pas confondre avec
// Bubble, id 'bubble' : le film de savon vu en macro, plein écran.)
//
// LE FILM, le détail décisif : chaque bulle est une sphère instanciée,
// légèrement déformée par un bruit lent (normale par différences finies sur la
// sphère, R5). Son épaisseur de film h (nm) varie : le film s'amincit en haut
// (drainage : 150 nm en haut, 750 nm en bas), un bruit s'écoule vers le bas, des
// tourbillons le font tourner autour de l'axe vertical. La couleur est
// l'interférence à deux ondes intégrée contre la CIE (lib/bubbles-film.js, la
// même loi que Bubble), lue à l'ÉPAISSEUR OPTIQUE h·cos θt : elle glisse avec
// l'angle de vue. Fresnel de l'eau (F0 = 0,02) : presque transparente au centre,
// colorée et réfléchissante au bord, où elle reflète les néons du club et une
// fenêtre lumineuse. Les deux faces sont dessinées (le reflet intérieur, plus
// faible). Additif sur le fond, cible MSAA privée : contours nets.
//
// LA VIE (lib/bubbles-sim.js) : dérive dans un courant d'air lent, légère
// montée, collisions élastiques (masse ∝ r³). ÉCLATEMENT : le film se déchire
// depuis un point (l'ouverture balaie la sphère en 80 ms, le bord de la
// déchirure, épaissi, brille) et projette une couronne de gouttelettes
// balistiques qui tombent. NAISSANCE : depuis le bas, la bulle grossit puis se
// détache.
//
// LE RYTHME (adapt.js) : TEMPS 1 à 2 éclatements (1 sur 2 temps en lounge),
// bokeh qui pulse ; MESURE vague de naissances ; PHRASE palette du fond ; DROP
// (party seulement) dix éclatements en cascade sur les croches, puis une nuée de
// petites bulles. SCÈNE (40 s) : la fenêtre du reflet tourne.
//
// Debug : ?bubblecount=<n>, ?bubblepop=1 (un éclatement toutes les 0,5 s),
// ?bubblefilm=1 (l'épaisseur en gris), ?bubblesprobe=1.

const TIER = { 0: { n: 20, msaa: 2, bgScale: 0.25 }, 1: { n: 44, msaa: 4, bgScale: 0.5 }, 2: { n: 60, msaa: 4, bgScale: 0.5 } };
const BOKEH_N = 28;
const SCENE_S = 40;
const SWARM_N = 24;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

const NOISE3 = `
  float h13(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
  float vn3(vec3 p) {
    vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(h13(i), h13(i + vec3(1, 0, 0)), f.x), mix(h13(i + vec3(0, 1, 0)), h13(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(h13(i + vec3(0, 0, 1)), h13(i + vec3(1, 0, 1)), f.x), mix(h13(i + vec3(0, 1, 1)), h13(i + vec3(1, 1, 1)), f.x), f.y), f.z);
  }
`;
// --- le fond : le club flou, en demi-résolution -----------------------------------
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const BG_FRAGMENT = `
  precision highp float;
  uniform vec3 uColA, uColB, uColC;
  uniform float uAspect, uTime, uPulse, uGain, uHasName;
  uniform sampler2D uName;
  uniform vec4 uBokeh[${BOKEH_N}];      // x, y (unités de hauteur, centre), rayon, phase
  varying vec2 vUv;
  vec3 pal(float k) { return k < 1.0 ? uColA : (k < 2.0 ? uColB : uColC); }
  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    // la salle : un fond sombre teinté, plus chaud en bas (le bar), un halo de scène en haut
    vec3 col = mix(uColB * 0.012, uColA * 0.03, smoothstep(1.0, -1.0, p.y)) + uColC * 0.035 * exp(-pow((p.y - 0.75) * 1.8, 2.0)) * exp(-p.x * p.x * 0.4);
    // le nom de l'artiste en néon, au fond, légèrement flou (lisible)
    // (lu hors de toute branche : dans une branche, les dérivées faussent le niveau de mip au bord du rectangle)
    vec2 nuv = vec2(p.x / 1.55 * 0.5 + 0.5, (p.y - 0.42) / 0.39 * 0.5 + 0.5);
    vec3 nameC = texture2D(uName, clamp(nuv, 0.0, 1.0), 2.2).rgb;
    float inName = step(0.0, nuv.x) * step(nuv.x, 1.0) * step(0.0, nuv.y) * step(nuv.y, 1.0) * uHasName;
    col += nameC * inName * 0.38 * (0.94 + 0.06 * sin(uTime * 7.0));
    // le bokeh : des disques doux au bord un peu plus vif (l'optique d'un objectif ouvert)
    for (int i = 0; i < ${BOKEH_N}; i++) {
      vec4 b = uBokeh[i];
      float d = length(p - b.xy) / b.z;
      if (d > 1.2) continue;
      float disc = smoothstep(1.0, 0.9, d) * (0.75 + 0.25 * smoothstep(0.55, 0.95, d));
      float pul = 0.7 + 0.3 * sin(b.w * 6.28318 + uTime * 0.7);
      col += pal(mod(float(i), 3.0)) * disc * 0.16 * pul * (1.0 + uPulse * (0.5 + 0.5 * sin(b.w * 17.0)));
    }
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;
const SHOW_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uSafeFloor;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather, uAspect;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
// --- les bulles ---------------------------------------------------------------------
const BUBBLE_VERTEX = `
  precision highp float;
  ${NOISE3}
  uniform sampler2D uState;
  uniform float uStateH, uTime;
  attribute float aIdx;
  varying vec3 vPw, vNw, vLocal;
  varying float vSeed, vPop, vBirth, vClip;
  varying vec3 vPopDir;
  vec4 st(float c) { return texture2D(uState, vec2((c + 0.5) / 4.0, (aIdx + 0.5) / uStateH)); }
  float bump(vec3 n, float s) { return 1.0 + 0.035 * (vn3(n * 1.6 + vec3(s, s * 0.7, uTime * 0.25)) - 0.5) * 2.0 + 0.02 * (vn3(n * 3.1 - vec3(uTime * 0.4, s, 0.0)) - 0.5) * 2.0; }
  void main() {
    vec4 A = st(0.0), B = st(1.0), C = st(2.0);
    vSeed = B.x; vPop = B.y; vBirth = B.z; vPopDir = C.xyz;
    vec3 n = normalize(position);
    vLocal = n;
    float r = A.w;
    // la déformation douce : rayon modulé par un bruit lent ; la normale par différences finies sur la sphère
    vec3 t1 = normalize(cross(n, abs(n.y) < 0.95 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0))), t2 = cross(n, t1);
    float e = 0.02;
    vec3 n1 = normalize(n + t1 * e), n2 = normalize(n + t2 * e);
    vec3 p0 = n * bump(n, vSeed), p1 = n1 * bump(n1, vSeed), p2 = n2 * bump(n2, vSeed);
    vec3 N = normalize(cross(p1 - p0, p2 - p0)); if (dot(N, n) < 0.0) N = -N;
    vec3 wp = A.xyz + p0 * r;
    vPw = wp; vNw = N;
    vec4 clip = projectionMatrix * viewMatrix * vec4(wp, 1.0);
    vClip = r > 0.0 ? 1.0 : 0.0;
    gl_Position = r > 0.0 ? clip : vec4(0.0, 0.0, -2.0, 1.0);
  }
`;
function bubbleFragment(film) {
  return `
  precision highp float;
  ${NOISE3}
  uniform sampler2D uLut;
  uniform vec3 uColA, uColB, uColC, uWin;
  uniform float uTime, uGain, uAspect, uSafeFeather;
  uniform vec2 uRes, uSafeCenter, uSafeHalfSize;
  varying vec3 vPw, vNw, vLocal;
  varying float vSeed, vPop, vBirth, vClip;
  varying vec3 vPopDir;
  // l'environnement que la bulle reflète : les néons du club (bandes verticales de la palette), une fenêtre lumineuse, le sol sombre
  vec3 env(vec3 d) {
    float az = atan(d.x, -d.z), el = d.y;
    // la salle elle-même : une lueur diffuse de la palette (le film se colore partout un peu)
    vec3 c = mix(uColB, uColA, 0.5 + 0.5 * d.y) * 0.13 + vec3(0.02);
    float bands = smoothstep(0.82, 0.98, 0.5 + 0.5 * sin(az * 5.0 + 0.6));
    float k = floor(mod((az + 3.14159) / 6.28318 * 5.0 + 0.6, 3.0));
    vec3 neon = k < 1.0 ? uColA : (k < 2.0 ? uColB : uColC);
    c += neon * bands * smoothstep(-0.25, 0.15, el) * smoothstep(0.85, 0.5, el) * 1.2;
    // la fenêtre : un rectangle doux, haut et de côté
    vec2 w = vec2(az - uWin.x, el - uWin.y);
    c += vec3(1.0, 0.97, 0.92) * smoothstep(0.22, 0.12, abs(w.x)) * smoothstep(0.3, 0.18, abs(w.y)) * 1.6;
    c += neon * 0.06 * smoothstep(0.2, -0.6, el);
    return c;
  }
  void main() {
    if (vClip < 0.5) discard;
    // la déchirure : depuis le point de rupture, l'ouverture balaie la sphère en ${(POP_S * 1000).toFixed(0)} ms
    float tear = 0.0;
    if (vPop >= 0.0) {
      float th = 3.14159 * smoothstep(0.0, ${POP_S.toFixed(3)}, vPop);
      float ang = acos(clamp(dot(vLocal, vPopDir), -1.0, 1.0));
      if (ang < th) discard;
      tear = smoothstep(th + 0.18, th, ang);                 // le bord du film qui se rétracte, épaissi
    }
    vec3 N = normalize(vNw), V = normalize(cameraPosition - vPw);
    float side = gl_FrontFacing ? 1.0 : -1.0;
    N *= side;                                                 // la face intérieure : la normale vers la caméra
    float cosI = clamp(dot(N, V), 0.0, 1.0);
    // l'épaisseur du film (nm) : drainage (mince en haut), écoulement vers le bas, tourbillons autour de l'axe vertical
    vec3 q = vLocal;
    float sw = uTime * 0.35 + q.y * 1.8 + vSeed;
    q.xz = mat2(cos(sw), -sin(sw), sin(sw), cos(sw)) * q.xz;
    float drain = mix(160.0, 760.0, smoothstep(0.95, -0.9, vLocal.y));
    float flow = vn3(q * 2.2 + vec3(vSeed, uTime * 0.22 + vSeed, 0.0)) * 0.65 + vn3(q * 5.5 + vec3(0.0, uTime * 0.4, vSeed)) * 0.35;
    float h = drain * (0.62 + 0.76 * flow);
    // le haut, le plus mince : la tache noire qui grandit avec l'âge de la bulle
    h *= mix(1.0, 0.25, smoothstep(0.86, 0.98, vLocal.y) * smoothstep(4.0, 20.0, vBirth));
    h = mix(h, 760.0, tear * 0.5);
    ${film ? 'gl_FragColor = vec4(vec3(h / ' + FILM_H_MAX.toFixed(1) + '), 1.0); return;' : ''}
    // l'épaisseur optique (réfraction dans le film) et la couleur d'interférence
    float hEff = h * sqrt(max(0.0, 1.0 - (1.0 - cosI * cosI) / (${N_FILM} * ${N_FILM})));
    vec3 filmC = texture2D(uLut, vec2(hEff / ${FILM_H_MAX.toFixed(1)}, 0.5)).rgb;
    // Fresnel de l'eau : presque rien au centre, tout au bord
    float F = 0.02 + 0.98 * pow(1.0 - cosI, 5.0);
    vec3 R = reflect(-V, N);
    vec3 envC = env(R);
    vec3 col = filmC * envC * (0.55 + 3.2 * F) + envC * F * 0.25;
    col *= side > 0.0 ? 1.0 : 0.55;                           // le reflet intérieur, plus faible
    col += vec3(1.0, 0.96, 0.9) * tear * 0.6;
    // la naissance : la bulle se forme, son film encore épais et sombre
    col *= smoothstep(0.0, ${BIRTH_S.toFixed(2)} * 0.6, vBirth);
    // le masque de la pochette (multiplie)
    vec2 s = (gl_FragCoord.xy / uRes * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 qq = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.25, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
  `;
}
// --- les gouttelettes : balistiques, calculées dans le shader --------------------------
const DROP_VERTEX = `
  precision highp float;
  uniform sampler2D uDrops;
  uniform float uDropsH, uTime;
  uniform vec2 uRes;
  attribute float aIdx;
  attribute vec2 aCorner;
  varying vec2 vC;
  varying float vA, vSize;
  void main() {
    vec4 A = texture2D(uDrops, vec2(0.25, (aIdx + 0.5) / uDropsH)), B = texture2D(uDrops, vec2(0.75, (aIdx + 0.5) / uDropsH));
    float u = uTime - A.w;
    if (A.w <= 0.0 || u < 0.0 || u > ${DROP_LIFE.toFixed(2)}) { gl_Position = vec4(0.0, 0.0, -2.0, 1.0); return; }
    vec3 p = A.xyz + B.xyz * u + vec3(0.0, 0.5 * ${GRAVITY.toFixed(2)} * u * u, 0.0);
    vec4 c = projectionMatrix * viewMatrix * vec4(p, 1.0);
    vSize = 1.2 + 2.2 * B.w;                                  // en pixels (R7 : une goutte fine fait 1 à 3 px)
    float half_ = vSize + 2.0;
    c.xy += aCorner * half_ / (uRes * 0.5) * c.w;
    vC = aCorner * half_;
    vA = (1.0 - smoothstep(${(DROP_LIFE * 0.6).toFixed(2)}, ${DROP_LIFE.toFixed(2)}, u)) * smoothstep(0.0, 0.03, u);
    gl_Position = c;
  }
`;
const DROP_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColA;
  uniform float uGain;
  varying vec2 vC;
  varying float vA, vSize;
  void main() {
    float d = length(vC);
    float core = 1.0 - smoothstep(vSize * 0.5, vSize, d);
    vec3 col = mix(uColA, vec3(1.0, 0.97, 0.93), 0.6) * core * 0.85 * vA;
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;

// =============================================================================

export class BubblesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIER[this._tier] || TIER[1];
    const nc = parseInt(urlParam('bubblecount'), 10);
    this._count = Number.isFinite(nc) && nc > 0 ? Math.min(MAX_BUBBLES - SWARM_N, nc) : this._L.n;
    this._forcePop = urlParam('bubblepop') === '1';
    this._film = urlParam('bubblefilm') === '1';
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.82, grain: 0.015, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    this._comfortL = new VdjComfort(VDJ_PRESETS.lounge.comfort);
    this._comfortP = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('bubblesprobe') === '1' && typeof window !== 'undefined') window.__bubbles = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    this._sim = new BubbleSim(this._count, 11);
    this._size = new THREE.Vector2(1280, 720);
    this._cam = new THREE.PerspectiveCamera(40, 16 / 9, 0.1, 50);
    this._cam.position.set(0, 0.1, 8.0); this._cam.lookAt(0, 0.1, -0.8);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    this._gainU = { value: 1 };
    // les textures d'état
    this._state = new Float32Array(4 * MAX_BUBBLES * 4);
    this._stateTex = new THREE.DataTexture(this._state, 4, MAX_BUBBLES, THREE.RGBAFormat, THREE.FloatType);
    this._stateTex.minFilter = this._stateTex.magFilter = THREE.NearestFilter; this._stateTex.needsUpdate = true;
    this._dropTex = new THREE.DataTexture(this._sim.drops, 2, MAX_DROPS, THREE.RGBAFormat, THREE.FloatType);
    this._dropTex.minFilter = this._dropTex.magFilter = THREE.NearestFilter; this._dropTex.needsUpdate = true;
    this._lut = new THREE.DataTexture(filmLutData(), FILM_LUT, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._lut.minFilter = this._lut.magFilter = THREE.LinearFilter; this._lut.wrapS = THREE.ClampToEdgeWrapping; this._lut.needsUpdate = true;
    this._pal = { a: new THREE.Vector3(1, 0.3, 0.7), b: new THREE.Vector3(0.3, 0.7, 1), c: new THREE.Vector3(1, 0.8, 0.3) };
    // le fond
    this._bgRT = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    this._bokeh = Array.from({ length: BOKEH_N }, () => new THREE.Vector4());
    this._bokehSeed = Array.from({ length: BOKEH_N }, (_, i) => ({ x: ((i * 0.618) % 1) * 2 - 1, y: ((i * 0.377 + 0.2) % 1) * 1.8 - 0.9, r: 0.05 + 0.13 * ((i * 0.731) % 1), ph: (i * 0.297) % 1, sp: 0.01 + 0.02 * ((i * 0.53) % 1) }));
    this._nameTex = null; this._nameKey = undefined;
    this._KU = { uColA: { value: this._pal.a }, uColB: { value: this._pal.b }, uColC: { value: this._pal.c }, uAspect: safeZoneUniforms.uAspect, uTime: { value: 0 }, uPulse: { value: 0 }, uGain: this._gainU, uHasName: { value: 0 }, uName: { value: null }, uBokeh: { value: this._bokeh } };
    this._bgScene = new THREE.Scene();
    const bq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._KU, vertexShader: QUAD_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false }));
    bq.frustumCulled = false; this._bgScene.add(bq);
    // la scène principale : le fond agrandi, les bulles, les gouttes
    this._scene = new THREE.Scene();
    this._showU = { uTex: { value: this._bgRT.texture } };
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._showU, vertexShader: QUAD_VERTEX, fragmentShader: 'precision mediump float; uniform sampler2D uTex; varying vec2 vUv; void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }', depthTest: false, depthWrite: false }));
    sq.frustumCulled = false; sq.renderOrder = -10; this._scene.add(sq);
    const sph = new THREE.SphereGeometry(1, 64, 44);
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', sph.attributes.position); g.setAttribute('normal', sph.attributes.normal); g.setIndex(sph.index);
    g.setAttribute('aIdx', new THREE.InstancedBufferAttribute(new Float32Array(Array.from({ length: MAX_BUBBLES }, (_, i) => i)), 1));
    g.instanceCount = MAX_BUBBLES;
    this._BU = { uState: { value: this._stateTex }, uStateH: { value: MAX_BUBBLES }, uTime: { value: 0 }, uLut: { value: this._lut }, uColA: { value: this._pal.a }, uColB: { value: this._pal.b }, uColC: { value: this._pal.c }, uWin: { value: new THREE.Vector3(-0.7, 0.45, 0) }, uGain: this._gainU, uRes: { value: new THREE.Vector2(1280, 720) },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._bubbleMat = new THREE.ShaderMaterial({ uniforms: this._BU, vertexShader: BUBBLE_VERTEX, fragmentShader: bubbleFragment(this._film), transparent: true, blending: this._film ? THREE.NormalBlending : THREE.AdditiveBlending, depthWrite: false, depthTest: false, side: THREE.DoubleSide });
    this._bubbles = new THREE.Mesh(g, this._bubbleMat); this._bubbles.frustumCulled = false; this._scene.add(this._bubbles);
    const dg = new THREE.InstancedBufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
    dg.setAttribute('aCorner', new THREE.BufferAttribute(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), 2));
    dg.setIndex([0, 1, 2, 2, 1, 3]);
    dg.setAttribute('aIdx', new THREE.InstancedBufferAttribute(new Float32Array(Array.from({ length: MAX_DROPS }, (_, i) => i)), 1));
    dg.instanceCount = MAX_DROPS;
    this._DU = { uDrops: { value: this._dropTex }, uDropsH: { value: MAX_DROPS }, uTime: { value: 0 }, uRes: this._BU.uRes, uColA: { value: this._pal.a }, uGain: this._gainU };
    this._drops = new THREE.Mesh(dg, new THREE.ShaderMaterial({ uniforms: this._DU, vertexShader: DROP_VERTEX, fragmentShader: DROP_FRAGMENT, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false }));
    this._drops.frustumCulled = false; this._drops.renderOrder = 5; this._scene.add(this._drops);
    // la cible MSAA privée, l'affichage, la mesure R10
    this._target = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: 'precision mediump float; uniform sampler2D uTex; varying vec2 vUv; void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }', depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.15; this._lumPending = false; this._lumAvg = 0.15; this._gain = 1; this._gainRendered = 1;

    this._adapt = new Adapt(); this._a = this._adapt.get();
    this._sceneClock = new SceneClock({ period: SCENE_S, start: 0 });
    this._time = 0; this._paused = false; this._rendered = false;
    this._sinceBeat = 9; this._beatN = 0; this._palShift = 0; this._winIdx = 0;
    this._cascade = []; this._swarmUntil = -1; this._forcePopT = 0;
    this._paletteSeen = undefined;
    this._stats = { beats: 0, bars: 0, phrases: 0, pops: 0, births: 0, drops: 0, cascades: 0 };
    this._applyPalette(null);
  }

  _applyPalette(pal) {
    if (pal === this._paletteSeen && pal !== null) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff, 0xffd23f];
    this._palHex = [v[0], v[1 % v.length], v[2 % v.length]];
    this._refreshColors();
  }
  _refreshColors() {
    const h = this._palHex, k = this._palShift % 3, c = new THREE.Color();
    vdjColor(h[k], c); this._pal.a.set(c.r, c.g, c.b);
    vdjColor(h[(k + 1) % 3], c); this._pal.b.set(c.r, c.g, c.b);
    vdjColor(h[(k + 2) % 3], c); this._pal.c.set(c.r, c.g, c.b);
    if (this._nameKey !== undefined) this._buildName(this._nameKey);
  }
  // le nom en néon : un tube coloré (trait large flou) et son coeur blanc
  _buildName(text) {
    if (typeof document === 'undefined') return;
    if (this._nameTex) { this._nameTex.dispose(); this._nameTex = null; }
    const W = 1024, H = 256, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d'); ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    const t = (text || '').trim(); if (!t) { this._KU.uHasName.value = 0; return; }
    let px = 150; const font = (p) => `600 ${p}px "Helvetica Neue", "Segoe Script", Arial, sans-serif`;
    ctx.font = font(px); const w = ctx.measureText(t).width; px = Math.min(px, px * (W * 0.88) / Math.max(1, w)); ctx.font = font(px);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const A = this._pal.a, css = `rgb(${Math.round(A.x * 255)},${Math.round(A.y * 255)},${Math.round(A.z * 255)})`;
    ctx.shadowColor = css; ctx.shadowBlur = px * 0.35; ctx.strokeStyle = css; ctx.lineWidth = Math.max(4, px * 0.09);
    ctx.strokeText(t, W / 2, H / 2); ctx.strokeText(t, W / 2, H / 2);
    ctx.shadowBlur = px * 0.12; ctx.lineWidth = Math.max(2, px * 0.03); ctx.strokeStyle = '#fff6ee'; ctx.strokeText(t, W / 2, H / 2);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.generateMipmaps = true; tex.minFilter = THREE.LinearMipmapLinearFilter;
    this._nameTex = tex; this._KU.uName.value = tex; this._KU.uHasName.value = 1;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    const nk = profile.artist || profile.title || '';
    if (nk !== this._nameKey) { this._nameKey = nk; this._buildName(nk); }
    const dt = this._paused ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    this._sceneClock.update(dt);
    if (this._paused) return;
    this._time += dt; this._sinceBeat += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 110, beatS = 60 / bpm;
    const party = a.intensity > 0.7;
    const sim = this._sim, rnd = sim.rnd;
    const d = profile.director || {};
    // TEMPS : 1 à 2 éclatements (lounge : un temps sur deux), le bokeh pulse
    if (a.beat) {
      this._stats.beats++; this._beatN++; this._sinceBeat = 0;
      const n = party ? (rnd() < 0.5 ? 2 : 1) : (this._beatN % 2 === 0 ? 1 : 0);
      for (let k = 0; k < n; k++) { const i = sim.pickPop(rnd); if (i >= 0 && sim.pop(i)) this._stats.pops++; }
    }
    if (this._forcePop) { this._forcePopT += dt; if (this._forcePopT > 0.5) { this._forcePopT = 0; const i = sim.pickPop(rnd); if (i >= 0 && sim.pop(i)) this._stats.pops++; } }
    // MESURE : une vague de naissances (jusqu'à la population voulue)
    if (a.bar) { this._stats.bars++; this._stats.births += sim.refill(party ? 8 : 5); }
    else if (sim.b.length < this._count * 0.6 && rnd() < dt * 2) this._stats.births += sim.refill(1);
    // PHRASE : la palette du fond ; SCÈNE : la fenêtre du reflet change de place
    if (a.phrase) { this._stats.phrases++; this._palShift++; this._refreshColors(); }
    if (this._sceneClock.wrapped) { this._winIdx++; const W = [[-0.7, 0.45], [0.8, 0.35], [2.4, 0.5], [-2.2, 0.3]][this._winIdx % 4]; this._BU.uWin.value.set(W[0], W[1], 0); }
    // DROP (party) : dix éclatements en cascade sur les croches, puis une nuée de petites bulles
    if (a.drop && a.dropReady && party) {
      this._stats.drops++; this._stats.cascades++;
      for (let k = 0; k < 10; k++) this._cascade.push(this._time + k * beatS / 2);
      this._swarmUntil = this._time + 10 * beatS / 2 + 4 * beatS;
      if (this._comfortP.requestFlash(this._time)) d.flash = VDJ_DROP_FLASH * 0.6;
    }
    while (this._cascade.length && this._cascade[0] <= this._time) { this._cascade.shift(); const i = sim.pickPop(rnd); if (i >= 0 && sim.pop(i)) this._stats.pops++; }
    if (this._swarmUntil > this._time && this._cascade.length === 0 && rnd() < dt * 10) {
      const b = sim.birth(); if (b) { b.r = b.rr = 0.08 + 0.08 * rnd(); this._stats.births++; }
    }
    sim.target = this._count + (this._swarmUntil > this._time ? SWARM_N : 0);
    sim.step(dt * (0.6 + 0.4 * a.motion), a.intensity);
    // les états : position et rayon, graine, âge de la déchirure, âge
    const S = this._state; S.fill(0);
    sim.b.forEach((b, i) => {
      if (i >= MAX_BUBBLES) return;
      const o = i * 16;
      S[o] = b.x; S[o + 1] = b.y; S[o + 2] = b.z; S[o + 3] = b.rr;
      S[o + 4] = b.seed; S[o + 5] = b.pop >= 0 ? sim.time - b.pop : -1; S[o + 6] = sim.time - b.born;
      S[o + 8] = b.popDir[0]; S[o + 9] = b.popDir[1]; S[o + 10] = b.popDir[2];
    });
    this._stateTex.needsUpdate = true;
    if (sim.dropsDirty) { this._dropTex.needsUpdate = true; sim.dropsDirty = false; }
    this._BU.uTime.value = sim.time; this._DU.uTime.value = sim.time;
    // le bokeh : dérive lente, pulsation au temps (lounge : la respiration de vdjLook)
    for (let i = 0; i < BOKEH_N; i++) { const s = this._bokehSeed[i]; const A = safeZoneUniforms.uAspect.value || 1.78; const x = ((s.x * A + this._time * s.sp + A) % (2 * A)) - A; this._bokeh[i].set(x, s.y + 0.05 * Math.sin(this._time * 0.3 + i), s.r, s.ph); }
    const breath = party ? Math.exp(-this._sinceBeat / (beatS * 0.35)) : vdjBeatBreath(this._sinceBeat) - 1;
    this._KU.uPulse.value = (party ? 0.9 : 3.0) * breath * Math.max(0.3, a.pulse);
    this._KU.uTime.value = this._time;
    // R10 : lounge 0,35, party 0,45
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const cap = 0.9 * (party ? 0.45 : 0.35);
    const gt = Math.min(1, cap / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    this._gainU.value = this._gain;
    this._postValue = this._post.update(party ? vdjBeatChroma(a.beatPhase, beatS) : 1);
    this._party = party;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      this._target = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa, depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
      this._dispU.uTex.value = this._target.texture; this._lumU.uTex.value = this._target.texture;
      this._bgRT.setSize(Math.max(16, Math.round(w * this._L.bgScale)), Math.max(16, Math.round(h * this._L.bgScale)));
    }
    if (this._paused && this._rendered) return;
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    this._cam.aspect = w / h; this._cam.updateProjectionMatrix();
    this._BU.uRes.value.set(w, h);
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._bgRT); renderer.render(this._bgScene, this._ortho);
    renderer.setRenderTarget(this._target); renderer.clear(true, false, false);
    renderer.render(this._scene, this._cam);
    this._gainRendered = this._gain; this._rendered = true;
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget); renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
  }

  getProbe() {
    const s = this._sim;
    return { tier: this._tier, target: s.target, count: s.b.length, popping: s.b.filter((b) => b.pop >= 0).length, party: !!this._party, intensity: +this._a.intensity.toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), cascade: this._cascade.length, stats: Object.assign({}, this._stats, { sim: s.stats }) };
  }
  // (sonde) la bulle visible la plus grande : centre et rayon à l'écran (pixels, y vers le bas)
  biggestOnScreen(w, h) {
    let best = null;
    for (const b of this._sim.b) {
      if (b.pop >= 0 || this._sim.time - b.born < BIRTH_S) continue;
      const c = new THREE.Vector3(b.x, b.y, b.z).project(this._cam), e = new THREE.Vector3(b.x + b.r, b.y, b.z).project(this._cam);
      const R = Math.abs(e.x - c.x) * 0.5 * w, X = (c.x * 0.5 + 0.5) * w, Y = (1 - (c.y * 0.5 + 0.5)) * h;
      if (X - R < 0 || X + R > w || Y - R < 0 || Y + R > h) continue;
      if (!best || R > best.r) best = { x: X, y: Y, r: R, b };
    }
    return best && { x: best.x, y: best.y, r: best.r };
  }

  dispose() {
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const rt of [this._target, this._bgRT, this._lumTarget]) if (rt) rt.dispose();
    for (const m of [this._bubbles, this._drops]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    for (const t of [this._stateTex, this._dropTex, this._lut, this._nameTex]) if (t) t.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__bubbles === this) window.__bubbles = null;
    this._sceneRef = null;
  }
}
